import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Brain, ArrowUpRight, TrendingUp } from 'lucide-react';
export function SEOvsAISEOSlide() {
  const [traffic, setTraffic] = useState(5000);
  // More conservative, research-backed model:
  // Local service business conversion rates (Unbounce benchmark report):
  // - Organic visitor → lead: 2-4% (form fill, phone call)
  // - Lead → customer: 15-25% close rate
  // - Average customer value varies, use $500 as baseline (conservative for services)
  //
  // Old SEO: 2.5% visitor-to-lead, 15% close = 0.375% visitor-to-customer
  // AI-SEO: 3.5% visitor-to-lead (better intent), 20% close (AI chatbot pre-qualifies) = 0.7%
  //
  // Revenue = traffic × 12 months × conversion × avg customer value ($500)
  const avgCustomerValue = 500; // Conservative baseline
  // Old SEO: 0.375% of visitors become customers
  const oldConversion = 0.00375;
  const oldCustomersPerYear = traffic * 12 * oldConversion;
  const oldRevenue = oldCustomersPerYear * avgCustomerValue;
  // AI-SEO: 0.7% of visitors become customers (better intent matching + AI chatbot)
  const aiConversion = 0.007;
  const aiCustomersPerYear = traffic * 12 * aiConversion;
  const aiRevenue = aiCustomersPerYear * avgCustomerValue;
  const additionalRevenue = aiRevenue - oldRevenue;
  const formatCurrency = (val: number) => {
    if (val >= 1000000) return '$' + (val / 1000000).toFixed(1) + 'M';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  return <section id="ai-seo" className="min-h-screen bg-dark-section py-24 px-4 flex flex-col justify-center" data-id="element-475">
      <div className="max-w-7xl mx-auto w-full" data-id="element-476">
        <div className="text-center mb-16" data-id="element-477">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-478">
            The New Game in 2026
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-479">
            Traditional SEO vs AI-SEO
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16" data-id="element-480">
          {/* Old SEO */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-white/5 rounded-2xl p-8 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-500" data-id="element-481">
            <div className="flex items-center mb-6" data-id="element-482">
              <div className="p-3 bg-white/10 rounded-lg mr-4" data-id="element-483">
                <Search size={32} data-id="element-484" />
              </div>
              <h3 className="text-2xl font-bold" data-id="element-485">Old SEO (2008 - 2025)</h3>
            </div>
            <ul className="space-y-4 text-text-muted mb-8" data-id="element-486">
              <li className="flex items-start" data-id="element-487">
                <span className="mr-2" data-id="element-488">•</span> Keyword stuffing & backlink
                farming
              </li>
              <li className="flex items-start" data-id="element-489">
                <span className="mr-2" data-id="element-490">•</span> Months to see results
              </li>
              <li className="flex items-start" data-id="element-491">
                <span className="mr-2" data-id="element-492">•</span> Constant algorithm updates
              </li>
            </ul>
            <div className="p-4 bg-white/5 rounded-xl border border-white/10" data-id="element-493">
              <span className="text-sm uppercase tracking-wider text-text-dim font-bold" data-id="element-494">
                Focus
              </span>
              <p className="text-lg font-medium mt-1" data-id="element-495">
                Ranking #1 for specific keywords
              </p>
            </div>
          </motion.div>

          {/* AI SEO */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark-card border border-primary/30 rounded-2xl p-8 relative overflow-hidden shadow-2xl shadow-primary/10" data-id="element-496">
            <div className="flex items-center mb-6" data-id="element-497">
              <div className="p-3 bg-primary/20 rounded-lg mr-4" data-id="element-498">
                <Brain className="text-primary" size={32} data-id="element-499" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-500">
                New AI-SEO (2026+)
              </h3>
            </div>
            <ul className="space-y-4 text-text-muted mb-8" data-id="element-501">
              <li className="flex items-start" data-id="element-502">
                <span className="mr-2 text-primary" data-id="element-503">✓</span> Content-first, AI
                chatbot integration
              </li>
              <li className="flex items-start" data-id="element-504">
                <span className="mr-2 text-primary" data-id="element-505">✓</span> Conversational
                search optimization
              </li>
              <li className="flex items-start" data-id="element-506">
                <span className="mr-2 text-primary" data-id="element-507">✓</span> Intent-based
                ranking
              </li>
            </ul>
            <div className="p-4 bg-primary/10 rounded-xl border border-primary/20" data-id="element-508">
              <span className="text-sm uppercase tracking-wider text-primary font-bold" data-id="element-509">
                Focus
              </span>
              <p className="text-lg font-medium mt-1 text-white" data-id="element-510">
                Capturing AI-generated traffic
              </p>
            </div>
          </motion.div>
        </div>

        {/* Data Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl p-8 border border-white/10 mb-16 text-center relative overflow-hidden" data-id="element-511">
          <div className="relative z-10" data-id="element-512">
            <p className="text-lg md:text-xl font-medium leading-relaxed" data-id="element-513">
              <span className="text-primary font-bold" data-id="element-514">Airbnb Data:</span> 14%
              of their 240M annual visitors use ChatGPT for travel planning.
              <br className="hidden md:block" data-id="element-515" />
              That's{' '}
              <span className="text-white font-bold" data-id="element-516">
                33.6 million potential customers
              </span>{' '}
              who've never hit your Google ranking.
            </p>
          </div>
        </motion.div>

        {/* Revenue Impact Calculator */}
        <div className="bg-dark-card rounded-2xl p-8 border border-white/10" data-id="element-517">
          <h3 className="text-2xl font-bold mb-2 text-center" data-id="element-518">
            Projected Revenue Impact
          </h3>
          <p className="text-text-muted text-center text-sm mb-8" data-id="element-519">
            Based on Unbounce conversion benchmarks • $500 avg customer value
          </p>

          <div className="mb-12" data-id="element-520">
            <label className="block text-sm font-medium text-text-muted mb-4 text-center" data-id="element-521">
              Monthly Website Traffic:{' '}
              <span className="text-white font-bold text-lg" data-id="element-522">
                {traffic.toLocaleString()}
              </span>
            </label>
            <input type="range" min="500" max="50000" step="500" value={traffic} onChange={e => setTraffic(parseInt(e.target.value))} className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-primary hover:accent-primary-hover" data-id="element-523" />
            <div className="flex justify-between text-xs text-text-dim mt-2" data-id="element-524">
              <span data-id="element-525">500</span>
              <span data-id="element-526">50,000</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-527">
            <div className="p-6 rounded-xl bg-white/5 border border-white/5" data-id="element-528">
              <h4 className="text-text-muted font-medium mb-4" data-id="element-529">
                Old SEO Approach
              </h4>
              <div className="space-y-4" data-id="element-530">
                <div className="flex justify-between" data-id="element-531">
                  <span className="text-sm" data-id="element-532">Visitor → Customer</span>
                  <span className="font-mono" data-id="element-533">0.38%</span>
                </div>
                <div className="flex justify-between" data-id="element-534">
                  <span className="text-sm" data-id="element-535">Customers / Year</span>
                  <span className="font-mono text-white" data-id="element-536">
                    {Math.round(oldCustomersPerYear)}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-537">
                  <span className="text-sm" data-id="element-538">Est. Revenue</span>
                  <span className="font-mono text-white" data-id="element-539">
                    {formatCurrency(oldRevenue)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-xl bg-primary/10 border border-primary/30 relative overflow-hidden" data-id="element-540">
              <div className="absolute top-0 right-0 p-2" data-id="element-541">
                <TrendingUp className="text-primary opacity-20" size={64} data-id="element-542" />
              </div>
              <h4 className="text-primary font-bold mb-4 flex items-center" data-id="element-543">
                AI-SEO (LetLois) <ArrowUpRight size={16} className="ml-2" data-id="element-544" />
              </h4>
              <div className="space-y-4 relative z-10" data-id="element-545">
                <div className="flex justify-between" data-id="element-546">
                  <span className="text-sm" data-id="element-547">Visitor → Customer</span>
                  <span className="font-mono text-white" data-id="element-548">
                    0.70% <span className="text-green-400 text-xs" data-id="element-549">(+87%)</span>
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-550">
                  <span className="text-sm" data-id="element-551">Customers / Year</span>
                  <span className="font-mono text-white" data-id="element-552">
                    {Math.round(aiCustomersPerYear)}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-553">
                  <span className="text-sm" data-id="element-554">Est. Revenue</span>
                  <span className="font-mono text-white text-lg font-bold" data-id="element-555">
                    {formatCurrency(aiRevenue)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <motion.div key={traffic} initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="mt-8 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-center" data-id="element-556">
            <p className="text-green-400 font-bold text-lg md:text-xl" data-id="element-557">
              Additional Revenue: {formatCurrency(additionalRevenue)} / year
            </p>
            <p className="text-text-dim text-xs mt-1" data-id="element-558">
              That's {Math.round(aiCustomersPerYear - oldCustomersPerYear)} more
              customers per year from the same traffic
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}